import './style.css';
import { type Capabilities, detectCapabilities } from './engine/core/capabilities';
import { Engine } from './engine/core/Engine';
import { flags } from './engine/core/flags';
import { createRenderer } from './engine/render/createRenderer';
import { screens } from './engine/ui/screens';
import { MENU_WORLD, startGame } from './game/Game';
import { shadersReady } from './game/ui/shadersReady';
import { showStillMenu } from './game/ui/startScreen';

/** A picture of the menu world, shown behind the menu until a world is loaded. */
const MENU_BACKDROP = `${import.meta.env.BASE_URL}menu-backdrop.jpg`;

async function main(): Promise<void> {
  screens.bootStatus('Looking for a WebGPU device…', 0.05);
  const caps = await detectCapabilities();
  if (!caps) {
    screens.unsupported('navigator.gpu is missing or no adapter was returned.');
    return;
  }
  console.info('[innerworlds] GPU', caps.adapter, caps);

  // A world named in the URL loads straight away. Otherwise the menu shows the live menu
  // world only if this browser already has its shaders compiled; the first time, it shows
  // a still picture instead of compiling for seconds (?menu=still|live forces either).
  const chosen = flags.strOrNull('scene');
  const menu = flags.strOrNull('menu');
  const still = !chosen && (menu === 'still' || (menu !== 'live' && !shadersReady(MENU_WORLD)));
  if (still) {
    showStillMenu(MENU_BACKDROP, () => {
      boot(caps, MENU_WORLD).catch((e) => screens.fatal(e));
    });
    return;
  }
  await boot(caps, chosen ?? MENU_WORLD);
}

async function boot(caps: Capabilities, sceneName: string): Promise<void> {
  screens.showBoot();
  screens.bootStatus('Starting the renderer…', 0.1);
  const canvas = document.getElementById('app') as HTMLCanvasElement;
  let renderer: Awaited<ReturnType<typeof createRenderer>>;
  try {
    renderer = await createRenderer(canvas, caps);
  } catch (e) {
    screens.unsupported(e instanceof Error ? e.message : String(e));
    return;
  }

  const engine = new Engine(renderer, canvas);
  await startGame(engine, caps, sceneName);
  engine.start();
}

main().catch((e) => screens.fatal(e));
